import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AgGrid,
  Button,
  Flex,
  Grid,
  ModalV2,
  RadioButton,
  Text,
  TextInput,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { toSentenceCaseLabel } from "../sentenceCaseLabel";
import {
  filterAttributeLeaves,
  getAttributeDataPath,
  type AttributeFilter,
  type AttributeLeaf,
} from "./attributeCatalogue";
import * as styles from "./styles_vanilla.css";

type SelectAttributeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (attributePath: string) => void;
  /** Path of an existing rule's attribute, when re-opening to edit. */
  initialPath?: string | null;
  /** Restrict the catalogue (e.g. by `dataType`) for field-level consumers. */
  attributeFilter?: AttributeFilter;
  attributeLeaves: ReadonlyArray<AttributeLeaf>;
};

type SelectAttributeBodyProps = {
  onClose: () => void;
  onConfirm: (attributePath: string) => void;
  initialPath: string | null;
  attributeFilter: AttributeFilter | undefined;
  attributeLeaves: ReadonlyArray<AttributeLeaf>;
};

type AttributeCellExtraParams = { searchTerm: string };

// Inner renderer for the auto-group column. Group rows fall through to a
// plain text node (the chevron + indentation come from AG Grid's surrounding
// group cell renderer); leaf rows render `[radio] {label}`. Both branches
// highlight the active search query.
//
// Selection state is mirrored into local state and synced via AG Grid's
// `rowSelected` node event. `reactiveCustomComponents` only re-renders cells
// on row-data changes (not selection changes), so reading `node.isSelected()`
// inline at render time would leave the radio out of sync after row clicks.
const AttributeCellRenderer = (
  params: AgGrid.ICellRendererParams<AttributeLeaf> & AttributeCellExtraParams,
) => {
  const node = params.node;
  const rawValue = typeof params.value === "string" ? params.value : "";
  const displayValue = toSentenceCaseLabel(rawValue);
  const searchTerm = params.searchTerm;
  const isLeaf = !node.group;
  const [isSelected, setIsSelected] = useState(() => Boolean(node.isSelected()));

  useEffect(() => {
    if (!isLeaf) return undefined;
    const sync = () => setIsSelected(Boolean(node.isSelected()));
    node.addEventListener("rowSelected", sync);
    return () => node.removeEventListener("rowSelected", sync);
  }, [node, isLeaf]);

  const label = (
    <Text variant="regular-md" asSpan>
      <HighlightText text={displayValue} searchValue={searchTerm} />
    </Text>
  );

  if (!isLeaf) return label;

  return (
    <Flex alignItems="center" gap={SPACING.SMALL}>
      <RadioButton
        name="select-attribute"
        checked={isSelected}
        onChange={() => node.setSelected(true)}
        tabIndex={-1}
      />
      {label}
    </Flex>
  );
};

const SelectAttributeBody = ({
  onClose,
  onConfirm,
  initialPath,
  attributeFilter,
  attributeLeaves,
}: SelectAttributeBodyProps) => {
  const [search, setSearch] = useState("");
  const [selectedPath, setSelectedPath] = useState<string | null>(initialPath);

  const rowData = useMemo(
    () => filterAttributeLeaves(search, attributeFilter, attributeLeaves),
    [search, attributeFilter, attributeLeaves],
  );

  const getDataPath = useCallback((data: AttributeLeaf) => getAttributeDataPath(data), []);

  const getRowId = useCallback(
    (params: AgGrid.GetRowIdParams<AttributeLeaf>) => params.data.path,
    [],
  );

  const isRowSelectable = useCallback(
    (node: AgGrid.IRowNode<AttributeLeaf>) => Boolean(node.data),
    [],
  );

  const onSelectionChanged = useCallback(
    (event: AgGrid.SelectionChangedEvent<AttributeLeaf>) => {
      const [first] = event.api.getSelectedRows();
      setSelectedPath(first?.path ?? null);
    },
    [],
  );

  // When re-opening in edit mode, select the existing attribute and expand
  // its parent group(s) so the user can see what's currently picked.
  const onGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<AttributeLeaf>) => {
      if (!initialPath) return;
      const node = event.api.getRowNode(initialPath);
      if (!node) return;
      node.setSelected(true);
      let parent: AgGrid.IRowNode<AttributeLeaf> | null = node.parent;
      while (parent && parent.level >= 0) {
        parent.setExpanded(true);
        parent = parent.parent;
      }
    },
    [initialPath],
  );

  const autoGroupColumnDef = useMemo<AgGrid.ColDef<AttributeLeaf>>(
    () => ({
      headerName: "Attribute",
      sortable: false,
      flex: 1,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: AttributeCellRenderer,
        innerRendererParams: { searchTerm: search } satisfies AttributeCellExtraParams,
      },
    }),
    [search],
  );

  // Collapsed by default; fully expanded when the user is searching.
  const groupDefaultExpanded = useMemo(() => (search.trim() ? -1 : 0), [search]);

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      domLayout: "autoHeight",
      rowHeight: 40,
      headerHeight: 0,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
      rowSelection: "single",
      suppressRowClickSelection: false,
      suppressContextMenu: true,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
    },
  });

  const handleConfirm = useCallback(() => {
    if (!selectedPath) return;
    onConfirm(selectedPath);
  }, [onConfirm, selectedPath]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
    [],
  );

  const handleClearSearch = useCallback(() => setSearch(""), []);

  const buttonLabel = initialPath ? "Update attribute" : "Add new attribute";
  const noResults = rowData.length === 0;

  return (
    <>
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <TextInput
            label="Attribute"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search attributes"
            icon="search"
            isClearable
            onClearValue={handleClearSearch}
            data-testid="select-attribute-search"
            isFullWidth
          />
          <div className={styles.attributeTree}>
            {noResults ? (
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={SPACING.X_SMALL}
                className={styles.attributeNoResults}
                data-testid="select-attribute-no-results"
              >
                <Icon type="search" size="x-large" color={Colors.Neutral600} />
                <Text variant="h4">No matches found</Text>
              </Flex>
            ) : (
              <Grid<AttributeLeaf>
                // AG Grid's in-place treeData diffing isn't reliable under
                // jsdom (and the catalogue is small enough that the remount
                // cost is negligible), so we key the grid on the search
                // term to force a clean re-render on every change.
                key={search}
                rowData={[...rowData]}
                getRowId={getRowId}
                columnDefs={[]}
                treeData
                getDataPath={getDataPath}
                autoGroupColumnDef={autoGroupColumnDef}
                groupDefaultExpanded={groupDefaultExpanded}
                isRowSelectable={isRowSelectable}
                onSelectionChanged={onSelectionChanged}
                onGridReady={onGridReady}
                gridOptions={gridOptions}
                reactiveCustomComponents
              />
            )}
          </div>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button variant="neutral" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            disabled={!selectedPath}
            onClick={handleConfirm}
            data-testid="select-attribute-confirm"
          >
            {buttonLabel}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </>
  );
};

export const SelectAttributeModal = ({
  isOpen,
  onClose,
  onConfirm,
  initialPath,
  attributeFilter,
  attributeLeaves,
}: SelectAttributeModalProps) => {
  // Re-mount the body whenever the modal opens or the editing target changes
  // — cheaper than orchestrating state resets via `useEffect`, and keeps
  // the body component free of `setState`-in-effect anti-patterns.
  const isEditMode = Boolean(initialPath);
  const bodyKey = `${isOpen ? "open" : "closed"}:${initialPath ?? ""}`;
  const title = isEditMode ? "Edit attribute" : "Select new attribute";
  const description = isEditMode
    ? "Select a different attribute for this rule"
    : "Select an attribute for this new rule";
  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title={title}
      description={description}
      size="medium"
      backdropClassName={styles.modalBackdrop}
      contentClassName={styles.modalContent}
      data-testid="select-attribute-modal"
    >
      <SelectAttributeBody
        key={bodyKey}
        onClose={onClose}
        onConfirm={onConfirm}
        initialPath={initialPath ?? null}
        attributeFilter={attributeFilter}
        attributeLeaves={attributeLeaves}
      />
    </ModalV2>
  );
};
